import { useCoachAvailability } from '../../../api/healthCoach/useCoachAvailability';
import { User } from '../../../models/user';
import { TZDate } from '@date-fns/tz';
import { ProviderType } from '../../../api/types';
import { useCoachPublicInfo } from '../../../api/public/useCoachPublicInfo';
import { useProviderAvailability } from '../../../api/healthCoach/useProviderAvailability';
import { useMemo } from 'react';
import { MeetingInfo, MeetingType } from '../../../models/meeting';
import { HostInfo } from '../types';

type Availability = {
  hostInfo: HostInfo;
  slots?: {
    date: string;
    times: string[]; // ISO strings
  }[];
};

type ReturnType = {
  data: Availability[] | undefined;
  isError: boolean;
  isLoading: boolean;
  meetingInfo: MeetingInfo | undefined;
};

type Props = {
  endDate: TZDate;
  meetingType: MeetingType | null;
  providerType: ProviderType | null;
  startDate: TZDate;
  // do not confuse this with user timezone:
  // they are different and the passed
  // timezone might be another one
  timezone: string;
  user: User | null;
};

export const useAvailability = ({
  endDate,
  meetingType,
  providerType,
  startDate,
  timezone,
  user,
}: Props): ReturnType => {
  const shouldFetchCoachData = providerType === ProviderType.Coach;

  // Setting these ids to undefined allows us to perform
  // only network calls actually related to the selected
  // provider type and avoid running the other ones.
  const coachId = shouldFetchCoachData ? user?.coach_id : undefined;
  const userId = shouldFetchCoachData ? undefined : user?.id;

  const {
    data: coachAvailability,
    isError: isCoachAvailabilityError,
    isLoading: isFetchingCoachAvailability,
  } = useCoachAvailability({
    care_model: user?.care_model,
    coach_uuid: coachId,
    endDate,
    meetingType,
    startDate,
    timezone,
  });

  const { data: hostInfo, isLoading: isFetchingHostInfo } =
    useCoachPublicInfo(coachId);

  const {
    data: providerAvailability,
    isError: isProviderAvailabilityError,
    isLoading: isFetchingProviderAvailability,
  } = useProviderAvailability({
    care_model: user?.care_model,
    userId,
    endDate,
    meetingType,
    providerType,
    startDate,
    timezone,
  });

  // Make sure that regardless data come from coach or provider,
  // it has the same shape for whom will use this hook
  const data = useMemo<Availability[] | undefined>(() => {
    const noCoachData = !hostInfo || !coachAvailability?.availability;
    if (shouldFetchCoachData && noCoachData) return [];

    if (shouldFetchCoachData && !!hostInfo) {
      return [
        {
          hostInfo: {
            id: `${hostInfo.healthie_id}`,
            display_name: hostInfo.display_name ?? '',
            image_url: hostInfo.image_url ?? '',
          },
          slots: coachAvailability?.availability,
        },
      ];
    }

    return providerAvailability?.providers?.map((provider) => ({
      hostInfo: {
        id: `${provider.provider_id}`,
        display_name: provider.display_name ?? '',
        image_url: provider.image_url ?? '',
      },
      slots: provider?.availability,
    }));
  }, [coachAvailability, hostInfo, providerAvailability, shouldFetchCoachData]);

  const meetingInfo = useMemo<MeetingInfo | undefined>(() => {
    if (shouldFetchCoachData) return coachAvailability?.meeting_type;
    return providerAvailability?.meeting_type;
  }, [
    coachAvailability,
    providerAvailability?.meeting_type,
    shouldFetchCoachData,
  ]);

  return {
    data,

    isError: shouldFetchCoachData
      ? isCoachAvailabilityError
      : isProviderAvailabilityError,

    isLoading: shouldFetchCoachData
      ? isFetchingCoachAvailability || isFetchingHostInfo
      : isFetchingProviderAvailability,

    meetingInfo,
  };
};
